import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { 
  FiMessageCircle, 
  FiUserCheck, 
  FiUserX, 
  FiUserMinus, 
  FiUser,
  FiClock,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { 
  ACCEPT_FRIEND_REQUEST,
  REJECT_FRIEND_REQUEST,
  REMOVE_FRIEND,
  BLOCK_USER
} from '../graphql/friends';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const FriendCard = ({ 
  relationship, 
  type = 'friend', // 'friend', 'pending_received', 'pending_sent'
  onUpdate,
  currentUserId,
  chatroom, // 聊天室數據（包含最後消息和未讀計數）
  onViewDetails // 查看使用者詳情的回調函數
}) => {
  const [loading, setLoading] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // GraphQL mutations
  const [acceptFriendRequest] = useMutation(ACCEPT_FRIEND_REQUEST, {
    refetchQueries: ['GetMyFriends', 'GetPendingRequests'],
    awaitRefetchQueries: true
  });
  const [rejectFriendRequest] = useMutation(REJECT_FRIEND_REQUEST, {
    refetchQueries: ['GetMyFriends', 'GetPendingRequests'],
    awaitRefetchQueries: true
  });
  const [removeFriend] = useMutation(REMOVE_FRIEND, {
    refetchQueries: ['GetMyFriends', 'GetPendingRequests'],
    awaitRefetchQueries: true
  });
  const [blockUser] = useMutation(BLOCK_USER, {
    refetchQueries: ['GetMyFriends', 'GetPendingRequests'],
    awaitRefetchQueries: true
  });

  // 確定顯示的使用者（不是當前使用者的那個）
  const otherUser = relationship.userA.id === currentUserId 
    ? relationship.userB 
    : relationship.userA;

  // 處理接受好友請求
  const handleAcceptRequest = async () => {
    setLoading(true);
    try {
      const { data } = await acceptFriendRequest({
        variables: { targetUserId: otherUser.id }
      });
      
      if (data.acceptFriendRequest.success) {
        onUpdate && onUpdate();
      } else {
        alert(data.acceptFriendRequest.message || '操作失敗');
      }
    } catch (error) {
      console.error('接受好友請求失敗:', error);
      alert('操作失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 處理拒絕好友請求
  const handleRejectRequest = async () => {
    setLoading(true);
    try {
      const { data } = await rejectFriendRequest({
        variables: { targetUserId: otherUser.id }
      });
      
      if (data.rejectFriendRequest.success) {
        onUpdate && onUpdate();
      } else {
        alert(data.rejectFriendRequest.message || '操作失敗');
      }
    } catch (error) {
      console.error('拒絕好友請求失敗:', error);
      alert('操作失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 處理取消已發送的請求（重用 rejectFriendRequest mutation）
  const handleCancelRequest = async () => {
    if (!window.confirm(`確定要取消發送給 ${otherUser.username} 的配對請求嗎？`)) {
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await rejectFriendRequest({
        variables: { targetUserId: otherUser.id }
      });
      
      if (data.rejectFriendRequest.success) {
        alert('配對請求已取消');
        onUpdate && onUpdate();
      } else {
        alert(data.rejectFriendRequest.message || '取消失敗');
      }
    } catch (error) {
      console.error('取消配對請求失敗:', error);
      alert('取消失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 處理移除好友
  const handleRemoveFriend = async () => {
    if (!window.confirm(`確定要移除好友 ${otherUser.username} 嗎？`)) {
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await removeFriend({
        variables: { targetUserId: otherUser.id }
      });
      
      if (data.removeFriend.success) {
        onUpdate && onUpdate();
      } else {
        alert(data.removeFriend.message || '操作失敗');
      }
    } catch (error) {
      console.error('移除好友失敗:', error);
      alert('操作失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 處理阻止使用者
  const handleBlockUser = async () => {
    if (!window.confirm(`確定要封鎖使用者 ${otherUser.username} 嗎？`)) {
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await blockUser({
        variables: { targetUserId: otherUser.id }
      });
      
      if (data.blockUser.success) {
        onUpdate && onUpdate();
      } else {
        alert(data.blockUser.message || '操作失敗');
      }
    } catch (error) {
      console.error('阻止使用者失敗:', error);
      alert('操作失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 格式化時間
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      return formatDistanceToNow(new Date(timeString), { 
        addSuffix: true, 
        locale: zhCN 
      });
    } catch (error) {
      return '';
    }
  };

  // 獲取關係狀態文本
  const getRelationshipStatus = () => {
    if (type === 'pending_received') return '發送了好友請求';
    if (type === 'pending_sent') return '等待回覆';
    if (type === 'friend') {
      const myType = relationship.userA.id === currentUserId 
        ? relationship.typeA 
        : relationship.typeB;
      
      switch (myType) {
        case 'FRIEND': return '好友';
        case 'CLOSE_FRIEND': return '密友';
        case 'BLOCKED': return '已阻止';
        default: return '未知狀態';
      }
    }
    return '';
  };

  // 獲取未讀消息數量
  const getUnreadCount = () => {
    console.log('🔍 [FriendCard] 檢查未讀計數:', {
      otherUserId: otherUser.id,
      otherUsername: otherUser.username,
      currentUserId,
      hasChatroom: !!chatroom,
      chatroomId: chatroom?.id,
      unreadCounts: chatroom?.unreadCounts,
      lastMessage: chatroom?.lastMessage
    });
    
    if (!chatroom?.unreadCounts) {
      console.log('⚠️ [FriendCard] 沒有 unreadCounts 數據');
      return 0;
    }
    
    const unreadItem = chatroom.unreadCounts.find(
      uc => uc.userId.id === currentUserId
    );
    
    console.log('📊 [FriendCard] 未讀項目:', {
      unreadItem,
      count: unreadItem?.count || 0
    });
    
    return unreadItem?.count || 0;
  };

  // 格式化最後一條消息
  const formatLastMessage = () => {
    if (!chatroom?.lastMessage) return null;
    
    const { content, senderId } = chatroom.lastMessage;
    const isMyMessage = senderId?.id === currentUserId;
    const prefix = isMyMessage ? '你: ' : '';
    const maxLength = 30;
    
    // 截斷過長的消息
    const truncated = content.length > maxLength 
      ? content.substring(0, maxLength) + '...' 
      : content;
    
    return prefix + truncated;
  };

  const hasUnread = getUnreadCount() > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-stretch justify-between">
        {/* 使用者信息 */}
        <div className="flex items-center">
          {/* 使用者名和狀態 */}
          <div>
            <div className="flex items-center space-x-2">
              {onViewDetails ? (
                <button
                  onClick={() => onViewDetails(otherUser.id)}
                  className="font-semibold text-gray-900 hover:text-blue-600 transition-colors flex items-center"
                >
                  {otherUser.username}
                  {/* 未讀訊息黃點標記 */}
                  {hasUnread && (
                    <span className="ml-2 inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                  )}
                </button>
              ) : (
                <Link 
                  to={`/user/${otherUser.id}`}
                  className="font-semibold text-gray-900 hover:text-blue-600 transition-colors flex items-center"
                >
                  {otherUser.username}
                  {/* 未讀訊息黃點標記 */}
                  {hasUnread && (
                    <span className="ml-2 inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                  )}
                </Link>
              )}
              
              {type === 'friend' && (
                <div className="relative">
                  <button
                    onClick={() => setShowActions(!showActions)}
                    className="inline-flex items-center px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    ⋯
                  </button>
                  
                  {showActions && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 origin-top-left">
                      <div className="py-1">
                        {onViewDetails ? (
                          <button
                            onClick={() => {
                              setShowActions(false);
                              onViewDetails(otherUser.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <FiUser className="w-4 h-4 mr-2" />
                            查看資料
                          </button>
                        ) : (
                          <Link
                            to={`/user/${otherUser.id}`}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setShowActions(false)}
                          >
                            <FiUser className="w-4 h-4 mr-2" />
                            查看資料
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            setShowActions(false);
                            handleRemoveFriend();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <FiUserMinus className="w-4 h-4 mr-2" />
                          移除好友
                        </button>
                        <button
                          onClick={() => {
                            setShowActions(false);
                            handleBlockUser();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <FiUserX className="w-4 h-4 mr-2" />
                          封鎖
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 顯示最後一條消息和時間 */}
            {type === 'friend' && chatroom?.lastMessage && (
              <div className="mt-1 space-y-0.5">
                <div className="text-sm text-gray-500">
                  {formatLastMessage()}
                </div>
                <div className="text-xs text-gray-400">
                  {formatTime(chatroom.lastMessage.sentAt)}
                </div>
              </div>
            )}
            
            {/* 如果沒有消息，顯示提示 */}
            {type === 'friend' && chatroom && !chatroom.lastMessage && (
              <div className="text-xs text-gray-400 mt-1">
                開始聊天吧
              </div>
            )}
            
            {/* 隱藏關係狀態顯示，避免重複 */}
            {false && (
              <div className="text-sm text-gray-500">
                {getRelationshipStatus()}
              </div>
            )}
            {/* 隱藏建立時間顯示 */}
            {false && relationship.establishedAt && (
              <div className="text-xs text-gray-400">
                建立於 {formatTime(relationship.establishedAt)}
              </div>
            )}
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex items-stretch space-x-2 -my-4 -mr-4">
          {type === 'pending_received' && (
            <>
              <button
                onClick={handleAcceptRequest}
                disabled={loading}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md transition-colors"
              >
                <FiCheck className="w-4 h-4 mr-1" />
                接受
              </button>
              <button
                onClick={handleRejectRequest}
                disabled={loading}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md transition-colors"
              >
                <FiX className="w-4 h-4 mr-1" />
                拒絕
              </button>
            </>
          )}

          {type === 'pending_sent' && (
            <button
              onClick={handleCancelRequest}
              disabled={loading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-md transition-colors"
            >
              <FiX className="w-4 h-4 mr-1" />
              {loading ? '取消中...' : '取消請求'}
            </button>
          )}

          {type === 'friend' && (
            <Link
              to={`/chat/${otherUser.id}`}
              className="inline-flex h-full items-center px-4 py-4 text-sm font-medium text-white bg-gradient-to-r from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 rounded-none rounded-r-lg transition-colors relative"
            >
              <FiMessageCircle className="w-4 h-4 mr-1" />
              聊天
              {/* 未讀消息標記 */}
              {getUnreadCount() > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-gray-900 bg-yellow-400 rounded-full min-w-[20px]">
                  {getUnreadCount()}
                </span>
              )}
            </Link>
          )}
        </div>
      </div>

      {/* 點擊外部關閉菜單 */}
      {showActions && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowActions(false)}
        />
      )}
    </div>
  );
};

export default FriendCard; 